import { Children, isValidElement, useState, type ReactNode } from 'react';
import Markdown, { type Components } from 'react-markdown';
import { isSafeHref } from './safeLink.js';

/**
 * Markdown for server-composed text (briefs). Spec §6 XSS rules: react-markdown
 * with raw HTML skipped (no rehype-raw), links only for https:, mailto: or
 * same-origin URLs and always `noopener noreferrer` in a new tab, no images.
 * Lists longer than three items collapse behind "Show N more".
 */

function CollapsingList({ ordered, children }: { ordered: boolean; children?: ReactNode }): JSX.Element {
  const [open, setOpen] = useState(false);
  const items = Children.toArray(children).filter(isValidElement);
  const shown = open ? items : items.slice(0, 3);
  const hidden = items.length - shown.length;
  const Tag = ordered ? 'ol' : 'ul';
  return (
    <Tag className="lo-md__list">
      {shown}
      {hidden > 0 && (
        <li className="lo-more-item">
          <button type="button" className="lo-more" onClick={() => setOpen(true)}>
            Show {hidden} more
          </button>
        </li>
      )}
    </Tag>
  );
}

const BASE: Components = {
  a: ({ href, children }) =>
    isSafeHref(href) ? (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    ) : (
      <span className="lo-link--plain">{children}</span>
    ),
  img: ({ alt }) => (alt ? <span>{alt}</span> : null),
  ul: ({ children }) => <CollapsingList ordered={false}>{children}</CollapsingList>,
  ol: ({ children }) => <CollapsingList ordered>{children}</CollapsingList>,
};

const INLINE: Components = { ...BASE, p: ({ children }) => <>{children}</> };

export default function SafeMarkdown({ children, inline = false }: { children: string; inline?: boolean }): JSX.Element {
  return (
    <Markdown skipHtml components={inline ? INLINE : BASE}>
      {children}
    </Markdown>
  );
}
